import classNames from "classnames";
import React, { useContext } from "react";
import { getNormalizedColSpans } from "./../../../../../ricos-common-plugins/layout/get-normalized-col-spans.js";
import {
  LAYOUT_DEFAULTS,
  normalizePadding
} from "./../../../../../ricos-common-plugins/layout/shared-with-viewer.js";
import { RicosContext, RicosNodeContextProvider } from "./../../../../../ricos-context/index.js";
import { getImageSrc } from "./../../../../../wix-rich-content-common/lib/imageUtils.js";
import styles from "./layout-scss.js";
const getGridTemplateColumnsTemplate = (node) => {
  const cols = [];
  node.nodes.forEach((cell) => {
    if (cell.type === "LAYOUT_CELL") {
      cols.push(cell.layoutCellData?.colSpan ?? 0);
    }
  });
  return getNormalizedColSpans(cols).map((col) => `minmax(0, ${col}fr)`).join(" ");
};
const getStyleContent = (node) => {
  const responsivenessBehaviour = {
    STACK: "stack",
    WRAP: "wrap"
  }[node.layoutData?.responsivenessBehaviour ?? LAYOUT_DEFAULTS.responsivenessBehaviour];
  const responsivenessBreakpoint = node.layoutData?.responsivenessBreakpoint && !isNaN(node.layoutData?.responsivenessBreakpoint) ? node.layoutData?.responsivenessBreakpoint : LAYOUT_DEFAULTS.responsivenessBreakpoint;
  const scopeValue = encodeURIComponent(
    `layout-${node.id}-${responsivenessBreakpoint}-${responsivenessBehaviour}`
  );
  const isWrapping = responsivenessBehaviour === "wrap";
  const styleContent = `
    [data-layout-scope=${scopeValue}].${styles.layout} {
      --ricos-internal-layout-display: ${isWrapping ? "flex" : "grid"};
      --ricos-internal-layout-justify-content: ${isWrapping ? "center" : "auto"};
      --ricos-internal-layout-grid-template: grid-template-columns: minmax(0, 1fr);
      --ricos-internal-layout-cell-min-width: ${isWrapping ? `${Math.floor(responsivenessBreakpoint / node.nodes.length)}px` : "100%"};
      --ricos-internal-layout-cell-height: ${isWrapping ? "auto" : "100%"};
    }
  `;
  return {
    scopeValue,
    content: `
@supports not (contain: inline-size) {
  @media only screen and (max-width: ${responsivenessBreakpoint}px) {
    ${styleContent}
  }
}

@container (max-width: ${responsivenessBreakpoint}px) {
  ${styleContent}
}
`
  };
};
const Layout = ({ node, nodeIndex, RicosNodesRenderer }) => {
  const { isMobile } = useContext(RicosContext);
  const style = React.useMemo(() => {
    if (node.type !== "LAYOUT") {
      return {};
    }
    const layoutData = node.layoutData;
    const backgroundImageUrl = layoutData?.backgroundImage?.media?.src?.url ?? (layoutData?.backgroundImage?.media?.src?.id && getImageSrc({ file_name: layoutData?.backgroundImage?.media?.src.id }, void 0, {
      requiredQuality: 100,
      requiredWidth: Math.min(layoutData?.backgroundImage?.media?.width ?? 4e3, 4e3),
      requiredHeight: Math.min(layoutData?.backgroundImage?.media?.height ?? 4e3, 4e3)
    }));
    const backdropImageUrl = layoutData?.backdropImage?.media?.src?.url ?? (layoutData?.backdropImage?.media?.src?.id && getImageSrc({ file_name: layoutData?.backdropImage?.media?.src.id }, void 0, {
      requiredQuality: 100,
      requiredWidth: Math.min(layoutData?.backdropImage?.media?.width ?? 4e3, 4e3),
      requiredHeight: Math.min(layoutData?.backdropImage?.media?.height ?? 4e3, 4e3)
    }));
    const cellPadding = normalizePadding(layoutData?.cellPadding, [...LAYOUT_DEFAULTS.cellPadding]);
    const cellVerticalAlignment = layoutData?.cellVerticalAlignment ?? LAYOUT_DEFAULTS.cellVerticalAlignment;
    const borderColor = layoutData?.borderColor ?? LAYOUT_DEFAULTS.borderColor;
    const borderWidth = layoutData?.borderWidth ?? LAYOUT_DEFAULTS.borderWidth;
    return {
      ...layoutData?.backgroundColor ? {
        "--ricos-internal-layout-background-color": layoutData.backgroundColor
      } : {},
      ...layoutData?.backgroundImage ? {
        "--ricos-internal-layout-background-image-src": `url(${backgroundImageUrl})`,
        ...layoutData.backgroundImage.opacity ? {
          "--ricos-internal-layout-background-image-opacity": `${layoutData.backgroundImage.opacity}%`
        } : {},
        ...layoutData.backgroundImage.scaling ? {
          "--ricos-internal-layout-background-image-scaling": layoutData.backgroundImage.scaling.toLowerCase()
        } : {},
        ...layoutData.backgroundImage.position ? {
          "--ricos-internal-layout-background-image-position": layoutData.backgroundImage.position.toLowerCase().split("_").join(" ")
        } : {}
      } : {},
      ...borderColor ? {
        "--ricos-internal-layout-border-color": borderColor,
        "--ricos-internal-layout-border-width": `${borderWidth}px`
      } : {},
      ...layoutData?.borderRadius ? {
        "--ricos-internal-layout-border-radius": `${layoutData.borderRadius}px`
      } : {},
      ...layoutData?.backdropColor ? {
        "--ricos-internal-layout-backdrop-color": layoutData.backdropColor
      } : {},
      ...layoutData?.backdropImage ? {
        "--ricos-internal-layout-backdrop-image-src": `url(${backdropImageUrl})`,
        ...layoutData.backdropImage.opacity ? {
          "--ricos-internal-layout-backdrop-image-opacity": `${layoutData.backdropImage.opacity}%`
        } : {},
        ...layoutData.backdropImage.scaling ? {
          "--ricos-internal-layout-backdrop-image-scaling": layoutData.backdropImage.scaling.toLowerCase()
        } : {},
        ...layoutData.backdropImage.position ? {
          "--ricos-internal-layout-backdrop-image-position": layoutData.backdropImage.position.toLowerCase().split("_").join(" ")
        } : {}
      } : {},
      ...typeof layoutData?.gap === "number" ? {
        "--ricos-internal-layout-gap": `${layoutData.gap}px`
      } : {},
      ...layoutData?.backdropPaddingTop && layoutData.backdropPaddingTop > 0 ? {
        "--ricos-internal-layout-backdrop-padding-top": `${layoutData.backdropPaddingTop}px`
      } : {},
      ...layoutData?.backdropPaddingBottom && layoutData.backdropPaddingBottom > 0 ? {
        "--ricos-internal-layout-backdrop-padding-bottom": `${layoutData.backdropPaddingBottom}px`
      } : {},
      ...cellPadding && cellPadding.length > 0 ? {
        "--ricos-internal-layout-cell-padding-top": `${cellPadding[0]}px`,
        "--ricos-internal-layout-cell-padding-right": `${cellPadding[1] ?? cellPadding[0]}px`,
        "--ricos-internal-layout-cell-padding-bottom": `${cellPadding[2] ?? cellPadding[0]}px`,
        "--ricos-internal-layout-cell-padding-left": `${cellPadding[3] ?? cellPadding[1] ?? cellPadding[0]}px`
      } : {},
      ...cellVerticalAlignment ? {
        "--ricos-internal-layout-cell-vertical-alignment": cellVerticalAlignment === "TOP" ? "flex-start" : cellVerticalAlignment === "BOTTOM" ? "flex-end" : "center"
      } : {}
    };
  }, [node]);
  const styleContent = React.useMemo(() => getStyleContent(node), [node]);
  return /* @__PURE__ */ React.createElement("div", { className: styles.layoutWrapper, style }, /* @__PURE__ */ React.createElement("style", { "data-layout-style": node.id }, styleContent.content), /* @__PURE__ */ React.createElement("div", { "data-breakout": "normal" }, /* @__PURE__ */ React.createElement(
    "div",
    {
      "data-layout-scope": styleContent.scopeValue,
      className: classNames(styles.layout, { [styles.mobile]: isMobile }),
      style: {
        "--ricos-internal-layout-column-template": getGridTemplateColumnsTemplate(node)
      }
    },
    /* @__PURE__ */ React.createElement(RicosNodeContextProvider, { parentNode: node, parentIndex: nodeIndex }, /* @__PURE__ */ React.createElement(RicosNodesRenderer, { key: `children-${node.id}`, nodes: node.nodes ?? [] }))
  )));
};
const LayoutRenderer = ({ node, ...props }) => {
  return /* @__PURE__ */ React.createElement(Layout, { key: node.id, node, ...props });
};
export {
  Layout,
  LayoutRenderer
};
//# sourceMappingURL=layout.js.map
